export enum AccountUse {
  'developer' = 'I am a developer hired to help build an app or a website',
  'entrepreneur' = 'I have an app or a website and I want to add the Bible to it',
  'ministry' = 'I am a ministry staff or representative building an app or a website',
  'student' = 'I am learning how to code',
  'hobby' = 'I am just checking things out',
  'other' = 'Other: Please specify below',
}

export const accountUseIsOrg = (accountUse: AccountUse): boolean =>
  accountUse === AccountUse.developer ||
  accountUse === AccountUse.entrepreneur ||
  accountUse === AccountUse.ministry ||
  accountUse === AccountUse.other;

export enum OrganizationType {
  'developer' = 'Professional Developer',
  'entrepreneur' = 'Entrepreneur',
  'ministry' = 'Christian Ministry',
  'personal' = 'Personal',
  'other' = 'Other',
}

export const acountUseOrganizationType = (accountUse: AccountUse): OrganizationType => {
  switch (accountUse) {
    case AccountUse.developer:
      return OrganizationType.developer;
    case AccountUse.entrepreneur:
      return OrganizationType.entrepreneur;
    case AccountUse.ministry:
      return OrganizationType.ministry;
    case AccountUse.student:
    case AccountUse.hobby:
      return OrganizationType.personal;
    default:
      return OrganizationType.other;
  }
};

export enum ApplicationType {
  'website' = 'Website',
  'mobileApp' = 'Mobile App',
  'desktopApp' = 'Desktop App',
  'crossPlatform' = 'Cross Platform',
  'other' = 'Other',
}

// Interfaces as split based on the various locations that hold this data,
// and do their best to synthesize this into two main interfaces that are
// exposed throughout the UI: User and Account;
//
// Cognito holds the user's id, email and account id,
// Portal API holds the rest of the user's info and can be access via one endpoint
// Portal API holds the user's account's info and can be access via another endpoint
//
// For each user and account there's an interface for the API return value, another
// for the fields that can be edited for each, and another that allows a partial update
// of each interface via the PUT API endpoints

// values from the auth token that UI cares about
export interface UserTokenClaims {
  id: string;
  email: string;
  emailVerified: boolean;
  accountId: string;
  isSuperAdmin: boolean;
}

// values from the account user api endpoint the UI cares about
export interface AccountUser {
  name: string;
  photo?: string;
}

// values managed by cognito
export interface AuthUserManagedFields {
  email?: string;
  oldPassword?: string;
  newPassword?: string;
}

// merged values that represent a user in our system
export interface User extends AccountUser, UserTokenClaims {}

// values that can be changed of a user via api + auth system
export interface EditableUser extends AccountUser, AuthUserManagedFields {}

// partial object that can be used by form payloads to update some part of a user
export type PartialEditableUser = Partial<EditableUser>;

// values from the plan api endpoint that the UI cares about
export interface Price {
  currency: string;
  unit_amount: number;
  nickname: string;
  usage_type?: 'licensed' | 'metered'; // from Stripe api
}

export interface Plan {
  id: string;
  name?: string;
  isActive: boolean;
  isUpcoming: boolean;
  label: string;
  color: string;
  features: string;
  description: string;
  dailyLimit: number;
  monthlyLimit: number;
  monthlyBaseLimit?: number;
  stripeProductId: string;
  prices?: Price[]; // This will be derived from stripeProductId each time, and not stored
  sortOrder: number;
  updatedAt: string;
  createdAt: string;
}

export interface TermsAndConditions {
  id: string;
  content: string;
  log: string;
  createdAt: string;
}

export interface AcceptedTermsAndConditions {
  id: string;
  date: string;
  userId: string;
}

// values that can be changed of an account via api
export interface EditableAccount {
  name?: string;
  use: string;
  useOther: string;
  website: string;
  acceptedTermsAndConditionsVersion: string;
}

export type PartialEditableAccount = Partial<EditableAccount>;

export interface Account extends Omit<EditableAccount, 'acceptedTermsAndConditionsVersion'> {
  id: string;
  type: string;
  stripeCustomerId?: string;
  adminUserId: string;
  acceptedTermsAndConditions?: AcceptedTermsAndConditions;
  createdAt?: string;
}

export enum PlanEnum {
  FREE = 'free',
  BASIC = 'basic',
  PREMIUM = 'premium',
  BYOL = 'byol',
  TEST = 'test', // used for testing only (should never be active)
}

export enum AppStatusEnum {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  DELETED = 'deleted',
  UNPAID = 'unpaid',
}

export enum CognitoGroupEnum {
  SUPER_ADMINS = 'super-admins-group',
}

// Applications
export interface AppInfo {
  name: string;
  description: string;
  type: ApplicationType;
  marketingUrl?: string;
  iconPath?: string;
  plan: PlanEnum;
}

export interface Application extends AppInfo {
  id: string;
  status: AppStatusEnum;
  apiKey: string;
  isActiveKey: boolean;
  dailyLimit: number;
  monthlyLimit: number;
  monthlyBaseLimit?: number;
  billingAnchorAt?: string;
  billingEndAt?: string;
  billingStartAt?: string;
  stripeSubscriptionId?: string;
  stripeInvoiceUrl?: string;
  dblToken: string;
  authorizedBibles: string[];
  createdAt: string;
}

export const isAllowedByolPlan = (dblToken: string, authorizedBibles: string[]): boolean =>
  dblToken !== '' || authorizedBibles.length > 0;

export const isAllowedLimitChange = (planId: PlanEnum): boolean => planId === PlanEnum.FREE;

export const isAllowedOwnLicense = (planId: PlanEnum): boolean =>
  planId === PlanEnum.FREE || planId === PlanEnum.BYOL;

export interface ApiKeyCounts {
  dailyCount: number;
  monthlyCount: number;
  countUpdatedAt?: string;
}

export interface AccountApps {
  apps: Application[];
  maxFreeAppsAllowed: number;
}

// Checkout
export interface CheckoutPrice {
  currency: string;
  unit_amount: number;
  nickname: string;
}

export interface CheckoutPlanPrice {
  basePrice: CheckoutPrice;
  plusPrice?: CheckoutPrice;
}

export interface BillingPortalSession {
  configuration: string;
  created: number;
  customer: string;
  id: string;
  livemode: boolean;
  object: string;
  on_behalf_of: string | null;
  return_url: string;
  url: string;
}

export type BibleType = '' | 'text' | 'audio';

export interface Bible {
  id: string;
  name: string;
  abbreviation?: string;
  language: string;
  type: string;
  isOpenAccess: boolean;
  isFairUse: boolean;
  isExpressLicensed: boolean;
}

export interface AccountAppDetails {
  account: Account;
  app: Application;
  apiKeyCounts: ApiKeyCounts;
}

export interface AppListRecord {
  accountId: string;
  accountName: string;
  accountType: string;
  appId: string;
  appName: string;
  appType: string;
  appApiKey: string;
  appPlan: string;
  appStatus: string;
  appCreatedAt: string;
  userName: string;
  userEmail: string;
  apiKeyCounts?: ApiKeyCounts;
}
